import { HubConnection, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import {
    ContactEventEntity,
    ContactParticipationFromJSON,
    ContactSummaryFromJSON,
    CustomerEntryFromJSON,
    GuidanceFromJSON,
    PartialUtteranceFromJSON,
    ProductRecommendationFromJSON,
    ScorecardFromJSON,
    SearchFromJSON,
    SearchResultFromJSON,
    SuggestionFromJSON,
    TaskSuggestionFromJSON,
    UtteranceFromJSON
} from "./api/sdk";
import { dispose, Disposable } from "@/utils/disposable";
import { useAuthStore } from "@/store/auth";
import { useClientConfig } from "@/store/client-config";
import { AuthenticationService } from "./authentication";

export const createContactCreatedChannel = () => "contacts:created";
export const createPartialUtteranceChannel = (contactId: number) => `contacts:${contactId}:utterance:partial`;
export const createUtteranceCreatedChannel = (contactId: number) => `contacts:${contactId}:utterance:created`;
export const createUtteranceUpdatedChannel = (contactId: number) => `contacts:${contactId}:utterance:updated`;
export const createContactUpdatedChannel = (contactId: number) => `contacts:${contactId}:updated`;
export const createContactSummaryCreatedChannel = (contactId: number) => `contacts:${contactId}:summary_created`;
export const createSuggestionCreatedChannel = (contactId: number) => `contacts:${contactId}:suggestion:created`;
export const createSuggestionUpdatedChannel = (contactId: number) => `contacts:${contactId}:suggestion:updated`;
export const createSearchCreatedChannel = (contactId: number) => `contacts:${contactId}:search:created`;
export const createSearchResultCreatedChannel = (searchId: number) => `search:${searchId}:result`;
export const createSearchResultAcceptedChannel = (searchId: number) => `search:${searchId}:result_accepted`;
export const createGuidanceCreatedChannel = (contactId: number) => `contacts:${contactId}:guidance:created`;
export const createCustomerEntryCreatedChannel = (customerId: number) => `customer:${customerId}:entry_created`;
export const createProductRecommendationCreatedChannel = (contactId: number) => `contact:${contactId}:product_recommendation_created`;
export const createContactEventCreatedChannel = (contactId: number) => `contact:${contactId}:event_created`;
export const createTaskSuggestionCreatedChannel = (contactId: number) => `contacts:${contactId}:task_suggestion:created`;
export const createContactProcessingStatusUpdatedChannel = (sourceId: string) => `contact:${sourceId}:processing_updated`;
export const createScorecardUpdatedChannel = (scorecardId: number) => `scorecard:${scorecardId}:updated`;

type Action<T> = (data: T) => void;
type Listener = Disposable & { channel: string; listen(): Promise<void>; };

let _recorder: (listener: Listener) => void = () => { };
const subscribedChannels = new Set<string>();  // Keep track of subscribed channels

function createListener(connection: HubConnection, channel: string, action: Action<any>) {
    const cb = (data: any) => {
        action(data);
    };

    if (subscribedChannels.has(channel)) {
        console.warn(`[WARNING] Already subscribed to channel: ${channel}`);
        return null; // If already subscribed, return null to prevent duplicate listeners
    }

    connection.on(channel, cb);
    subscribedChannels.add(channel); // Add to subscribed channels set

    let listening = false;

    const listener: Listener = {
        channel,
        async listen() {
            listening = true;
            await connection.invoke('Subscribe', [channel]);
        },
        dispose() {
            connection.off(channel, cb);
            if (listening) {
                connection.invoke('Unsubscribe', [channel]);
                subscribedChannels.delete(channel); // Remove from the set when unsubscribed
            }
        },
    };
    _recorder(listener);

    return listener;
}

function createEventHandlerWithParam<TData, TParam>(connection: HubConnection, channelBuilder: (param: TParam) => string, parser: (data: any) => TData) {
    return (param: TParam, action: Action<TData>): Listener | null => {
        const channel = channelBuilder(param);
        return createListener(connection, channel, (data: any) => action(parser(data)));
    };
}

function createEventHandler<T>(connection: HubConnection, channel: string, parser: (data: any) => T) {
    return (action: Action<T>): Listener | null => {
        return createListener(connection, channel, (data: any) => action(parser(data)));
    };
}

let connection: HubConnection | null = null;
let connectingPromise: Promise<HubConnection> | null = null;

export function useConnection() {
    async function getConnection() {
        if (connection && connection.state === HubConnectionState.Connected) {
            return connection;
        }

        if (!connectingPromise) {
            connectingPromise = new Promise(async (resolve) => {
                const authStore = useAuthStore();
                const clientConfig = useClientConfig();
                if (AuthenticationService.requiresTokenRefresh()) {
                    await AuthenticationService.handleTokenRefresh();
                }

                const conn = new HubConnectionBuilder()
                    .withUrl(import.meta.env.VITE_API_URL + "/app/hubs/contact/", {
                        accessTokenFactory: () =>
                            JSON.stringify({
                                bearerToken: authStore.bearerToken || "",
                                tenantId: clientConfig.selectedTenant,
                            }),
                    })
                    .withAutomaticReconnect()
                    .build();

                await conn.start();

                // Only set the connection once it is connected
                connection = conn;
                conn.onclose((err) => {
                    console.error('closed', err)
                });
                resolve(connection);
            });
        }
        return await connectingPromise;
    }

    async function closeConnection() {
        if (connection) {
            await connection.stop();
            connection = null;
        }
    }

    return { getConnection, closeConnection };
}

export function createWS(connection: HubConnection) {
    const onContactCreated = createEventHandler(connection, createContactCreatedChannel(), ContactParticipationFromJSON);
    const onPartialUtteranceCreated = createEventHandlerWithParam(connection, createPartialUtteranceChannel, PartialUtteranceFromJSON);
    const onUtteranceCreated = createEventHandlerWithParam(connection, createUtteranceCreatedChannel, UtteranceFromJSON);
    const onUtteranceUpdated = createEventHandlerWithParam(connection, createUtteranceUpdatedChannel, UtteranceFromJSON);
    const onContactUpdated = createEventHandlerWithParam(connection, createContactUpdatedChannel, (data: any) => data as number);
    const onContactSummaryCreated = createEventHandlerWithParam(connection, createContactSummaryCreatedChannel, (data: any) => data as number);
    const onSuggestionCreated = createEventHandlerWithParam(connection, createSuggestionCreatedChannel, SuggestionFromJSON);
    const onSuggestionUpdated = createEventHandlerWithParam(connection, createSuggestionUpdatedChannel, SuggestionFromJSON);
    const onSearchCreated = createEventHandlerWithParam(connection, createSearchCreatedChannel, SearchFromJSON);
    const onSearchResultCreated = createEventHandlerWithParam(connection, createSearchResultCreatedChannel, SearchResultFromJSON);
    const onSearchResultAccepted = createEventHandlerWithParam(connection, createSearchResultAcceptedChannel, (data: any) => data as any);
    const onGuidanceCreated = createEventHandlerWithParam(connection, createGuidanceCreatedChannel, GuidanceFromJSON);
    const onCustomerEntryCreated = createEventHandlerWithParam(connection, createCustomerEntryCreatedChannel, CustomerEntryFromJSON);
    const onProductRecommendationCreated = createEventHandlerWithParam(connection, createProductRecommendationCreatedChannel, ProductRecommendationFromJSON);
    const onContactEventCreated = createEventHandlerWithParam(connection, createContactEventCreatedChannel, (data: any) => { return data.data as ContactEventEntity });
    const onTaskSuggestionCreated = createEventHandlerWithParam(connection, createTaskSuggestionCreatedChannel, TaskSuggestionFromJSON);
    const onContactProcessingStatusUpdated = createEventHandlerWithParam(connection, createContactProcessingStatusUpdatedChannel, ContactSummaryFromJSON);
    const onScorecardUpdated = createEventHandlerWithParam(connection, createScorecardUpdatedChannel, ScorecardFromJSON);

    const setupListeners = async (setup: () => void): Promise<Disposable> => {
        const listeners: Listener[] = [];
        _recorder = (l) => {
            if (l) listeners.push(l); // Only push if listener is not null (prevents duplicates)
        };
        setup();
        _recorder = () => { };

        const uniqueChannels = [...new Set(listeners.map(x => x.channel))];

        await connection.invoke("Subscribe", uniqueChannels);

        return {
            dispose() {
                dispose(listeners);
                connection.invoke("Unsubscribe", uniqueChannels);
                uniqueChannels.forEach(channel => subscribedChannels.delete(channel)); // Remove from subscribedChannels on dispose
            }
        };
    };

    return {
        setupListeners,
        onContactCreated,
        onPartialUtteranceCreated,
        onUtteranceCreated,
        onUtteranceUpdated,
        onContactUpdated,
        onSuggestionCreated,
        onSuggestionUpdated,
        onSearchCreated,
        onSearchResultCreated,
        onSearchResultAccepted,
        onGuidanceCreated,
        onCustomerEntryCreated,
        onProductRecommendationCreated,
        onContactSummaryCreated,
        onContactEventCreated,
        onTaskSuggestionCreated,
        onContactProcessingStatusUpdated,
        onScorecardUpdated
    };
}

export function useWS() {

}
